import {
    DELETE,
    createOrUpdatePublishedWatchlist,
    createWatchlist,
    deleteWatchlist,
    fetchPublishedWatchlist,
    fetchPublishedWatchlists,
    fetchWatchlistItems,
    fetchWatchlists,
    unwatch,
    watch,
} from 'utils/api/watchlist.api';
import { useQuery, useQueryClient } from 'react-query';
import { useEffect } from 'react';
import useOptimisticMutation from './useOptimisticMutation';
import { v4 as uuid } from 'uuid';

const watchlistItemsKey = 'watchlistItems';
const watchlistsKey = 'watchlists';
const publishedWatchlistsKey = 'published_watchlist';
const options = { staleTime: 30000 };

export const usePrefetchWatchlistsAndItems = () => {
    const qc = useQueryClient();

    useEffect(() => {
        qc.prefetchQuery(watchlistsKey, fetchWatchlists, options);
        qc.prefetchQuery(watchlistItemsKey, fetchWatchlistItems, options);
    }, []);

    return null;
};

export const useFetchWatchlistItems = () =>
    useQuery(watchlistItemsKey, fetchWatchlistItems, options);

export const useFetchWatchlists = () =>
    useQuery(watchlistsKey, fetchWatchlists, options);

const optimisticAddItem = (watchlistItems = [], { id, watchlist, data }) =>
    watchlistItems.findIndex(
        ({ id: currentId, watchlist: currentWatchlist }) =>
            id === currentId && watchlist === currentWatchlist,
    ) < 0
        ? [...watchlistItems, { id, watchlist, modified: Date.now(), ...data }]
        : watchlistItems;

const optimisticRemoveItem = (watchlistItems = [], { id, watchlist }) =>
    watchlistItems.filter(
        ({ id: currentId, watchlist: currentWatchlist }) =>
            id !== currentId || watchlist !== currentWatchlist,
    );

const optimisticAddItems = (watchlistItems, items) =>
    items.reduce(optimisticAddItem, watchlistItems);
const optimisticRemoveItems = (watchlistItems, items) =>
    items.reduce(optimisticRemoveItem, watchlistItems);

export const useMutateWatchlistItems = () => {
    const { mutateAsync: add } = useOptimisticMutation(
        watchlistItemsKey,
        watch,
        optimisticAddItems,
        false,
    );
    const { mutateAsync: remove } = useOptimisticMutation(
        watchlistItemsKey,
        unwatch,
        optimisticRemoveItems,
        false,
    );

    return { add, remove };
};

const optimisticAddWatchlist = (
    watchlists = {},
    { id, action = null, data },
) => {
    if (action !== DELETE)
        return {
            ...watchlists,
            [id]: { id, action, ...data },
        };

    const { [id]: maybeWatchlist, ...restWatchlists } = watchlists;
    if (maybeWatchlist) return restWatchlists;
    return watchlists;
};

const optimisticRemoveWatchlist = (watchlists, options) =>
    optimisticAddWatchlist(watchlists, { ...options, action: DELETE });

export const useMutateWatchlists = () => {
    const { mutateAsync: add } = useOptimisticMutation(
        watchlistsKey,
        createWatchlist,
        optimisticAddWatchlist,
        false,
    );
    const { mutateAsync: remove } = useOptimisticMutation(
        watchlistsKey,
        deleteWatchlist,
        optimisticRemoveWatchlist,
        false,
    );

    return { add, remove };
};

export const usePublishedWatchlist = (id) =>
    useQuery([publishedWatchlistsKey, id], fetchPublishedWatchlist, options);

export const usePublishedWatchlists = () =>
    useQuery(publishedWatchlistsKey, fetchPublishedWatchlists, options);

const optimisticCreateOrUpdatePublishedWatchlist = (
    prevCache,
    { id, watchlist: source_id, read: read_permission },
) => {
    if (!id)
        return [
            ...prevCache.slice(),
            {
                id: uuid(),
                source_id,
                read_permission,
                optimisticId: true,
            },
        ];

    const idx = prevCache.findIndex(({ id: currId }) => id === currId);
    return idx < 0
        ? prevCache
        : [
              ...prevCache.slice(0, idx),
              { ...prevCache[idx], read_permission },
              ...prevCache.slice(idx + 1),
          ];
};

export const useCreateOrUpdatePublishedWatchlist = () => {
    const { mutateAsync: publish, isLoading } = useOptimisticMutation(
        publishedWatchlistsKey,
        createOrUpdatePublishedWatchlist,
        optimisticCreateOrUpdatePublishedWatchlist,
    );

    return { publish, isLoading };
};
